import { useIntl } from 'react-intl'
import _get from 'lodash/get';
import cx from 'classnames';
import s from './ShortStats.module.scss';
import { useSelector } from 'react-redux';
import { useWatcherLink } from '@/processes/WatcherLink';
import { selectPollingInterval } from '@/processes/Settings';
import { useNavigation } from '@/processes/Router';
import { useCryptoFormatter, useThreshold } from '@/processes/PoolStats';
import TextBlock from '@/shared/ui/TextBlock';
import Divider from '@/shared/ui/Divider';
import NextPayout, { useNextPayout } from '@/entities/NextPayout';
import { useGetRewardsStatsQuery, useGetDailyEarningsQuery } from '@/shared/api/Stats';
import { useGetBalanceIdQuery, useGetTransactionsQuery } from '@/shared/api/Payouts';
import { useMinerAccount } from "@/processes/MinerAccounts";
import { usePayoutData } from '@/shared/hooks/usePayoutData';

type ShortStatsProps = {
  className?: string;
}

const ShortStats = (props: ShortStatsProps) => {
  const {
    className = '',
  } = props;
  const intl = useIntl();
  const pollingInterval = useSelector(selectPollingInterval);
  const { coin, isETC, isLTCBTC } = useNavigation();
  const { isWatcherLink } = useWatcherLink();
  const { threshold } = useThreshold({ coin })
  const { account, miner } = useMinerAccount();
  const { formatCryptoAmount } = useCryptoFormatter({ coin })

  const payoutData = usePayoutData();
  const actualThreshold = _get(payoutData, 'threshold', threshold) || threshold;

  const {
    data: rewardsStats = {}, 
    isLoading: isRewardsStatsLoading,
  } = useGetRewardsStatsQuery({ account, coin, miner }, { 
    pollingInterval,
    skip: !(account || miner),
  })
  const { 
    data: balanceId,
    isLoading: isBalanceIdLoading,
  } = useGetBalanceIdQuery({ account, coin, miner }, { skip: !(account || miner) })
  const { 
    latestTransaction,
    isLoading: isTransactionLoading,
  } = useGetTransactionsQuery({ 
    coin,
    balanceId,
    account,
    miner,
    limit: 1, 
    offset: 0,
  }, {
    pollingInterval, skip: !coin,
    selectFromResult: ({ isLoading, data = [] }) => (
      {
        latestTransaction: data || [],
        isLoading,
      }
    ),
  })

  const {
    data: dailyEarnings,
    isLoading: isDailyEarningsLoading,
  } = useGetDailyEarningsQuery({ account, coin, miner }, { skip: !(account || miner)})

  const formatDailyEarnings = formatCryptoAmount(_get(dailyEarnings, 'amount', 0));
  const total = _get(rewardsStats, 'total', 0);
  const unpaid = _get(rewardsStats, 'unpaid', 0);
  const currentAmount = Number(_get(dailyEarnings, 'amount', 0));
  const latestTransactionAmount = _get(latestTransaction, '0.value', 0);
  const { progress, progressValue } = useNextPayout({
    unpaid,
    estDailyEarnings: Number(formatDailyEarnings),
    currentAmount,
    threshold: actualThreshold, 
    coin 
  });

  return (
    <>
      <Divider className='hidden sm:block mt-6 mb-4'/>
      <div className={cx(s.root, className)}>
        <TextBlock
          isLoading={isDailyEarningsLoading}
          rootClassName={s.item}
          title={intl.formatMessage({ id: 'stats.est.earnings' })}
          value={formatDailyEarnings}
          subValue={isLTCBTC ? 'BTC' : coin}
        />
        <TextBlock
          isLoading={isRewardsStatsLoading}
          rootClassName={s.item}
          title={intl.formatMessage({ id: 'stats.total' })}
          value={formatCryptoAmount(total)}
          subValue={isLTCBTC ? 'BTC' : coin}
        />
        {isWatcherLink ? null : (
          <TextBlock
            isLoading={isTransactionLoading || isBalanceIdLoading}
            rootClassName={s.item}
            title={intl.formatMessage({ id: 'stats.last.payout' })}
            value={formatCryptoAmount(latestTransactionAmount)}
            subValue={isLTCBTC ? 'BTC' : coin}
          />
        )}
        <TextBlock
          rootClassName={s.item}
          title={intl.formatMessage({ id: 'stats.threshold' })}
          value={actualThreshold}
          subValue={isLTCBTC ? 'BTC' : coin}
        />
        <NextPayout
          className={s.item}
          progress={progress}
          payoutTitle={intl.formatMessage({ id: 'stats.next.payout' })}
          payoutValue={progressValue}
        />
      </div>
    </>
  );
}

export default ShortStats;
